import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FileText, Code2 } from 'lucide-react';
import {
  useGetOnboardingQuery,
  useUpdateOnboardingMutation,
} from '../../../store/api/onboardingApi';
import WizardCard from '../WizardCard';

/**
 * Screen 3A / 3B. Branches on the primary use case (onboarding_path). Per the
 * approved approach, the wizard does not copy the invoice / API-key tools: it
 * hands off to the REAL tool via a deep link (?onboarding=1) and the merchant
 * returns through the "Back to setup" banner there. The other path is always
 * offered as a secondary link so the merchant is never trapped.
 */
const Activate = () => {
  const navigate = useNavigate();
  const { data: state, isLoading } = useGetOnboardingQuery();
  const [updateOnboarding, { isLoading: isSaving }] = useUpdateOnboardingMutation();

  const [path, setPath] = useState('invoice');

  useEffect(() => {
    if (state?.onboarding_path) setPath(state.onboarding_path);
  }, [state]);

  // Persist the chosen path and mark activation reached, then open the tool.
  const goToTool = async (chosen, destination) => {
    try {
      await updateOnboarding({
        onboarding_path: chosen,
        onboarding_status: 'activation_done',
      }).unwrap();
      navigate(destination);
    } catch (err) {
      toast.error(err?.message || 'Something went wrong. Please try again.');
    }
  };

  // Switch path without leaving the step; persist so resume keeps the choice.
  const switchPath = async (chosen) => {
    setPath(chosen);
    try {
      await updateOnboarding({ onboarding_path: chosen }).unwrap();
    } catch {
      // Non-blocking; the local view already switched.
    }
  };

  if (isLoading) {
    return (
      <section className="wiz-card" aria-busy="true">
        <div className="wiz-skeleton" style={{ width: '70%', marginBottom: 12 }} />
        <div className="wiz-skeleton" style={{ width: '85%', marginBottom: 24 }} />
        <div className="wiz-skeleton block" style={{ height: 120 }} />
      </section>
    );
  }

  if (path === 'api') {
    return (
      <WizardCard
        title="Here is your test API key"
        subtitle="Use it to create your first payment. This is Test mode."
        primaryLabel="Open developer tools"
        onPrimary={() => goToTool('api', '/dashboard/developer?onboarding=1')}
        primaryLoading={isSaving}
        onBack={() => navigate('/onboarding/plan')}
        secondary={
          <button type="button" onClick={() => switchPath('invoice')}>
            Prefer to start with an invoice instead?
          </button>
        }
      >
        <div className="wiz-handoff">
          <span className="wiz-handoff-icon" aria-hidden="true">
            <Code2 size={22} />
          </span>
          <p>
            We will open your developer tools, where you can copy your test API
            key and a ready to use code snippet to create a payment. You can also
            set up webhooks and read the docs there. Come back here when you are
            ready to make a test payment.
          </p>
        </div>
      </WizardCard>
    );
  }

  return (
    <WizardCard
      title="Let's create your first invoice"
      subtitle="This is a test invoice. No real money moves."
      primaryLabel="Create test invoice"
      onPrimary={() => goToTool('invoice', '/dashboard/invoices?onboarding=1')}
      primaryLoading={isSaving}
      onBack={() => navigate('/onboarding/use-cases')}
      secondary={
        <button type="button" onClick={() => switchPath('api')}>
          Prefer to start with the API instead?
        </button>
      }
    >
      <div className="wiz-handoff">
        <span className="wiz-handoff-icon" aria-hidden="true">
          <FileText size={22} />
        </span>
        <p>
          We will open the invoice builder with a sample $10.00 test invoice and
          accepted test tokens. Edit it or create it as is, then come back here to
          make a test payment.
        </p>
      </div>
    </WizardCard>
  );
};

export default Activate;
